import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { TaskFilters, TaskTabs, TaskGrid } from "./TaskBundle";
import { AgentFilters, AgentTabs, AgentGrid } from "./AgentBundle";
import MapPanel from "./MapPanel";
import AssignModal from "../AssignModal";
import TaskDetailsModal from "./TaskDetailsModal";
import SendNotificationModal from "../../Driver/SendNotificationModal";
import { CheckCircle, XCircle } from "lucide-react";
import { Toaster } from "sonner";

export default function MapDashboard({
  tasks,
  agents,
  teams,
  selectedTeam,
  setSelectedTeam,
  loading,
  isLoading,
  fromDate,
  toDate,
  shops,
  setFromDate,
  setToDate,
  FetchTaskCall,
  fetchTasks,
  FetchDriverCall,
  fetchDrivers,
  isAddTaskOpen,
  fetchTaskLogs,
  handleChangeStatus,
  SyncWithAnlook,
  AssignTaskToDriver,
  handleToggle,
  isChecked,
  baseurl,
  token,
  setIsUnauthorised,
  fetchCustomerInstructions,
  instructionTaskIds,
}) {
  // ================= UI STATE =================
  const [selectedTaskTab, setSelectedTaskTab] = useState("unassigned");
  const [selectedStatus, setSelectedStatus] = useState("All Status");
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const [agentSearchQuery, setAgentSearchQuery] = useState("");
  const [selectedShop1, setSelectedShop1] = useState(""); // task-side shop filter
  const [selectedShop, setSelectedShop] = useState(""); // agent-side shop filter
  const [selectedAgentTab, setSelectedAgentTab] = useState("all drivers");

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [taskToAssign, setTaskToAssign] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState("");

  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [localNewStatus, setLocalNewStatus] = useState("");

  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationAgent, setNotificationAgent] = useState(null);

  const [isMapZoomed, setIsMapZoomed] = useState(false);

  const [instructionModalOpen, setInstructionModalOpen] = useState(false);
  const [taskInstructions, setTaskInstructions] = useState([]);
  const [loadingInst, setLoadingInst] = useState(false);

  // date handling
  const [tempFromDate, setTempFromDate] = useState(fromDate);
  const [tempToDate, setTempToDate] = useState(toDate);
  const [appliedFromDate, setAppliedFromDate] = useState(fromDate);
  const [appliedToDate, setAppliedToDate] = useState(toDate);

  // logs modal state
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [assignmentLogs, setAssignmentLogs] = useState([]);
  const [statusChangeLogs, setStatusChangeLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);

  // assign modal
  const [assignTab, setAssignTab] = useState("team");
  const [selectedAssignTeam, setSelectedAssignTeam] = useState("");

  // ================= HELPERS =================
  const isDateInRange = useCallback((taskDate, fromDate, toDate) => {
    if (!taskDate) return true;
    if (!fromDate && !toDate) return true;

    const taskDateObj = new Date(taskDate);
    const fromDateObj = fromDate ? new Date(fromDate) : null;
    const toDateObj = toDate ? new Date(toDate) : null;

    if (fromDateObj && taskDateObj < fromDateObj) return false;
    if (toDateObj && taskDateObj > toDateObj) return false;

    return true;
  }, []);

  const normalizeStatus = (s = "") =>
    s?.toLowerCase().replace(/\s+/g, " ").trim();

  // ================= TASK FILTERING =================

  const baseFilteredTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];

    let filtered = [...tasks];

    // ---- SHOP FILTER ----
    if (selectedShop1) {
      filtered = filtered.filter((task) => {
        const taskShopCode =
          task?.shopcode || task?.shop_code || task?.restaurant_code;

        return taskShopCode?.toString() === selectedShop1.toString();
      });
    }

    // ---- DATE FILTER ----
    if (appliedFromDate || appliedToDate) {
      filtered = filtered.filter((task) =>
        isDateInRange(
          task?.created_at || task?.createdAt || task?.date,
          appliedFromDate,
          appliedToDate,
        ),
      );
    }

    return filtered;
  }, [tasks, selectedShop1, appliedFromDate, appliedToDate, isDateInRange]);

  const statusFilteredTasks = useMemo(() => {
    let filtered = [...baseFilteredTasks];

    if (selectedStatus !== "All Status") {
      const filter = normalizeStatus(selectedStatus);

      const statusMap = {
        unassigned: ["unassigned"],

        assigned: ["assigned", "started", "inprogress"],

        acknowledged: ["acknowledged"],

        arrived: ["arrived"],

        "food is ready": ["food is ready"],

        "picked up": ["picked up"],

        completed: ["successful"],

        cancelled: ["cancelled", "failed", "declined"],
      };

      const allowedStatuses = statusMap[filter];

      if (allowedStatuses) {
        filtered = filtered.filter((task) =>
          allowedStatuses.includes(normalizeStatus(task?.status)),
        );
      }
    }

    return filtered;
  }, [baseFilteredTasks, selectedStatus]);

  const filteredTasks = useMemo(() => {
    let result = [...statusFilteredTasks];

    // ---- TAB FILTER ----
    if (selectedTaskTab === "unassigned") {
      result = result.filter((t) => t?.status?.toLowerCase() === "unassigned");
    }

    if (selectedTaskTab === "completed") {
      result = result.filter((t) => t?.status?.toLowerCase() === "successful");
    }

    if (selectedTaskTab === "assigned") {
      result = result.filter((t) =>
        ["assigned", "started", "inprogress"].includes(
          t?.status?.toLowerCase(),
        ),
      );
    }

    if (selectedTaskTab === "cancelled") {
      result = result.filter((t) =>
        ["cancelled", "failed", "declined"].includes(t?.status?.toLowerCase()),
      );
    }

    // ---- SEARCH FILTER ----
    if (taskSearchQuery.trim()) {
      const q = taskSearchQuery.toLowerCase();
      result = result.filter((t) =>
        JSON.stringify(t).toLowerCase().includes(q),
      );
    }

    return result;
  }, [statusFilteredTasks, selectedTaskTab, taskSearchQuery]);

  // ================= AGENT FILTERING =================
  const filteredAgents = useMemo(() => {
    let list = agents || [];

    if (selectedTeam && selectedTeam !== "All Team") {
      const team = teams.find((t) => t.team_name === selectedTeam);
      const ids = team?.driverlist?.map((d) => d.id?.toString()) || [];

      list = list.filter(
        (a) =>
          ids.includes(a?.id?.toString()) ||
          ids.includes(a?.driverId?.toString()),
      );
    }

    if (selectedShop) {
      const teamsWithShop = teams.filter((t) =>
        t.shopList?.some(
          (s) => s.shop_code?.toString() === selectedShop.toString(),
        ),
      );

      const validIds = teamsWithShop.flatMap(
        (t) => t.driverlist?.map((d) => d.id?.toString()) || [],
      );

      list = list.filter(
        (a) =>
          validIds.includes(a?.id?.toString()) ||
          validIds.includes(a?.driverId?.toString()),
      );
    }

    if (selectedAgentTab !== "all drivers") {
      list = list.filter(
        (a) => a?.dutyStatus?.toLowerCase() === selectedAgentTab,
      );
    }

    if (agentSearchQuery.trim()) {
      const q = agentSearchQuery.toLowerCase();
      list = list.filter(
        (a) => a?.fullName?.toLowerCase().includes(q) || a?.mobile?.includes(q),
      );
    }

    return list;
  }, [
    agents,
    teams,
    selectedTeam,
    selectedShop,
    selectedAgentTab,
    agentSearchQuery,
  ]);

  // ================= DRIVERS FOR ASSIGN =================
  const teamDrivers = useMemo(() => {
    if (!selectedTeam || selectedTeam === "All Team") return [];

    const teamData = teams.find((t) => t.team_name === selectedTeam);

    const ids = teamData?.driverlist?.map((d) => d.id?.toString()) || [];

    return (agents || []).filter(
      (a) =>
        ids.includes(a?.id?.toString()) ||
        ids.includes(a?.driverId?.toString()),
    );
  }, [selectedTeam, teams, agents]);

  const allTeamDrivers = useMemo(() => {
    if (!selectedAssignTeam) return [];

    const teamData = teams.find((t) => t.team_name === selectedAssignTeam);

    const ids = teamData?.driverlist?.map((d) => d.id?.toString()) || [];

    return (agents || []).filter(
      (a) =>
        ids.includes(a?.id?.toString()) ||
        ids.includes(a?.driverId?.toString()),
    );
  }, [selectedAssignTeam, teams, agents]);

  // ================= ACTIONS =================
  const handleAssignTask = async () => {
    if (!selectedAgentId || !taskToAssign) return;

    const success = await AssignTaskToDriver(
      taskToAssign.pk_task_id,
      selectedAgentId,
    );

    if (success) {
      setShowAssignModal(false);
      setSelectedAgentId("");
      await fetchTasks(fromDate, toDate, () => { });
    }
  };

  // ================= EFFECTS =================
  useEffect(() => {
    if (!selectedTeam) setSelectedTeam("All Team");
  }, [selectedTeam, setSelectedTeam]);

  useEffect(() => {
    setSelectedShop("");
  }, [selectedTeam]);

  useEffect(() => {
    if (selectedTask?.status) {
      setLocalNewStatus(selectedTask.status.toLowerCase());
    }
  }, [selectedTask]);

  useEffect(() => {
    const mapStatusToTab = {
      "All Status": "all",
      Unassigned: "unassigned",
      Assigned: "assigned",
      Acknowledged: "acknowledged",
      Arrived: "arrived",
      "Food is Ready": "food is ready",
      "Picked up": "picked up",
      Completed: "completed",
      Cancelled: "cancelled",
    };

    if (mapStatusToTab[selectedStatus]) {
      setSelectedTaskTab(mapStatusToTab[selectedStatus]);
    }
  }, [selectedStatus]);

  return (
    <div className="flex w-full min-h-screen  gap-4">
      {/* LEFT PANEL */}
      <div className="flex-1  p-2 overflow-y-auto min-w-[800px]">
        <div className="text-black flex justify-between font-semibold text-2xl bg-gray z-10">
          <span>Tasks</span>

          <div
            role="tablist"
            aria-label="Switch Auto/Manual"
            className={`relative inline-grid grid-cols-2 w-[88px] rounded-full bg-gray-100 border border-gray-200 p-1 ${loading ? "opacity-60 pointer-events-none" : ""
              }`}
          >
            {/* Sliding pill */}
            <span
              aria-hidden="true"
              className="absolute top-1 bottom-1 left-1 rounded-full bg-white shadow transition-transform duration-300 ease-in-out pointer-events-none"
              style={{
                width: "calc(50% - 0.25rem)",
                transform: isChecked ? "translateX(100%)" : "translateX(0%)",
              }}
            />

            {/* Manual (left) */}
            <button
              type="button"
              role="tab"
              aria-selected={!isChecked}
              className="relative z-10 flex items-center justify-center px-1 py-1 rounded-full"
              onClick={() => handleToggle(false)}
            >
              <XCircle
                className={`h-4 w-4 ${!isChecked ? "text-red-500" : "text-gray-500"
                  }`}
              />
            </button>

            {/* Auto (right) */}
            <button
              type="button"
              role="tab"
              aria-selected={isChecked}
              className="relative z-10 flex items-center justify-center px-1 py-1 rounded-full"
              onClick={() => handleToggle(true)}
            >
              <CheckCircle
                className={`h-4 w-4 ${isChecked ? "text-green-600" : "text-gray-500"
                  }`}
              />
            </button>
          </div>
        </div>

        <Toaster position="top-right" />
        <div className="sticky top-0 z-10 ">
          <TaskFilters
            taskSearchQuery={taskSearchQuery}
            setTaskSearchQuery={setTaskSearchQuery}
            shops={shops}
            loading={loading}
            selectedShop1={selectedShop1}
            setSelectedShop1={setSelectedShop1}
            tempFromDate={tempFromDate}
            tempToDate={tempToDate}
            setTempFromDate={setTempFromDate}
            setTempToDate={setTempToDate}
            onApply={() => {
              setAppliedFromDate(tempFromDate);
              setAppliedToDate(tempToDate);
              setFromDate(tempFromDate);
              setToDate(tempToDate);
            }}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
          />

          <TaskTabs
            selectedTaskTab={selectedTaskTab}
            setSelectedTaskTab={setSelectedTaskTab}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            getTaskTabCount={(tab) => {
              const baseList = baseFilteredTasks;

              switch (tab) {
                case "all":
                  return baseList.length;

                case "unassigned":
                  return baseList.filter(
                    (t) => t?.status?.toLowerCase() === "unassigned",
                  ).length;

                case "completed":
                  return baseList.filter(
                    (t) => t?.status?.toLowerCase() === "successful",
                  ).length;

                case "assigned":
                  return baseList.filter(
                    (t) => t?.status?.toLowerCase() === "assigned",
                  ).length;

                case "cancelled":
                  return baseList.filter((t) =>
                    ["cancelled", "failed", "declined"].includes(
                      t?.status?.toLowerCase(),
                    ),
                  ).length;

                default:
                  return baseList.filter(
                    (t) => t?.status?.toLowerCase() === tab,
                  ).length;
              }
            }}
          />

          <TaskGrid
            isLoading={isLoading}
            isStatusChanging={false}
            tasks={filteredTasks}
            onAssignClick={(task) => {
              setTaskToAssign(task);
              setShowAssignModal(true);
            }}
            onDetailsClick={(task) => {
              const taskId = task.pk_task_id || task.id;

              console.log("OPENING TASK:", taskId);

              setSelectedTask(task);
              setShowDetailsModal(true);

              setAssignmentLogs([]);
              setStatusChangeLogs([]);
              setShowLogs(false);

              fetchTaskLogs(
                taskId,
                fromDate,
                toDate,
                setAssignmentLogs,
                setStatusChangeLogs,
              );
            }}
            SyncWithAnlook={SyncWithAnlook}
            instructionTaskIds={instructionTaskIds}
            onInstructionClick={async (taskId) => {
              setLoadingInst(true);

              const allData = await fetchCustomerInstructions();

              const onlyThisTask = (allData || []).filter(
                (i) => String(i.task_id) === String(taskId),
              );

              setTaskInstructions(onlyThisTask);
              setInstructionModalOpen(true);
              setLoadingInst(false);
            }}
            getShopName={(code) =>
              shops?.find(
                (s) =>
                  s.shop_code === code ||
                  s.code === code ||
                  s.restaurant_code === code,
              )?.shop_name
            }
          />
        </div>
      </div>

      {instructionModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 ">
          <div className="bg-white p-5 rounded-xl w-[520px] shadow-lg">
            <h2 className="text-lg font-bold mb-3">Customer Instructions</h2>

            {loadingInst ? (
              <p>Loading...</p>
            ) : Array.isArray(taskInstructions) &&
              taskInstructions.length > 0 ? (
              taskInstructions.map((i) => (
                <div
                  key={i.pk_customer_delivery_instruction_id}
                  className="mb-3 border-b pb-3"
                >
                  <p className="font-semibold text-base">
                    {i.instuction_title || "Instruction"}
                  </p>

                  <p className="text-sm text-gray-600 mt-1">
                    <strong>Default note:</strong>{" "}
                    {i.instuction_description || "No description"}
                  </p>

                  {i.customer_instruction_messege && (
                    <p className="text-sm text-orange-700 mt-1">
                      <strong>Customer message:</strong>{" "}
                      {i.customer_instruction_messege}
                    </p>
                  )}

                  <p className="text-xs text-gray-600 mt-1">
                    Shop: {i.shop_name || "N/A"} • Task: {i.task_id}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No instructions for this task.</p>
            )}

            <div className="mt-3 flex justify-end">
              <button
                onClick={() => {
                  setInstructionModalOpen(false);
                  setTaskInstructions([]);
                }}
                className="mt-3 bg-red-500 text-white px-4 py-2 rounded flex justify-end"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RIGHT PANEL */}
      <div className="w-[450px]  flex flex-col h-[calc(100vh-80px)]">
        <AgentFilters
          teams={teams}
          selectedTeam={selectedTeam}
          setSelectedTeam={setSelectedTeam}
          selectedShop={selectedShop}
          setSelectedShop={setSelectedShop}
          loading={loading}
          agentSearchQuery={agentSearchQuery}
          setAgentSearchQuery={setAgentSearchQuery}
          // FetchDriverCall={FetchDriverCall}
          FetchDriverCall={fetchDrivers}
        />

        <AgentTabs
          selectedAgentTab={selectedAgentTab}
          setSelectedAgentTab={setSelectedAgentTab}
          getAgentTabCount={(tab) => {
            const baseList = agents || [];

            if (tab === "all drivers") return baseList.length;

            return baseList.filter((a) => a?.dutyStatus?.toLowerCase() === tab)
              .length;
          }}
        />

        <AgentGrid
          agents={filteredAgents}
          onNotify={(agent) => {
            setNotificationAgent(agent);
            setNotificationModalOpen(true);
          }}
        />

        {!isAddTaskOpen && (
          <MapPanel
            tasks={filteredTasks}
            drivers={filteredAgents}
            isMapZoomed={isMapZoomed}
            setIsMapZoomed={setIsMapZoomed}
          />
        )}
      </div>

      {/* ASSIGN MODAL */}
      <AssignModal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        teams={teams}
        assignTab={assignTab}
        setAssignTab={setAssignTab}
        selectedAssignTeam={selectedAssignTeam}
        setSelectedAssignTeam={setSelectedAssignTeam}
        teamDrivers={teamDrivers}
        allTeamDrivers={allTeamDrivers}
        selectedAgentId={selectedAgentId}
        setSelectedAgentId={setSelectedAgentId}
        handleAssignTask={handleAssignTask}
      />

      {/* TASK DETAILS MODAL */}
      <TaskDetailsModal
        show={showDetailsModal}
        task={selectedTask}
        onClose={() => setShowDetailsModal(false)}
        assignmentLogs={assignmentLogs}
        statusChangeLogs={statusChangeLogs}
        showLogs={showLogs}
        setShowLogs={setShowLogs}
        onFetchLogs={async (taskId) => {
          const logs = await fetchTaskLogs(taskId);
          setAssignmentLogs(logs.assignmentLogs);
          setStatusChangeLogs(logs.statusChangeLogs);
        }}
        onStatusChange={async (taskId, newStatus) => {
          const success = await handleChangeStatus(
            taskId,
            newStatus,
            selectedTask?.driver_id,
          );

          if (success) {
            setShowDetailsModal(false);
          }
        }}
      />

      {/* NOTIFICATION MODAL */}
      {notificationModalOpen && (
        <SendNotificationModal
          isOpen={notificationModalOpen}
          onClose={() => setNotificationModalOpen(false)}
          driver={notificationAgent}
          baseurl={baseurl}
          token={token}
          setIsUnauthorised={setIsUnauthorised}
        />
      )}
    </div>
  );
}
